import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getMe, type Me } from './api';
import { useSession } from './auth';

type MeContextValue = {
  me: Me | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const MeContext = createContext<MeContextValue | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setMe(await getMe());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load profile');
      setMe(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!session.isPending && session.data) {
      void load();
    } else if (!session.isPending && !session.data) {
      setMe(null);
      setLoading(false);
    }
  }, [session.isPending, session.data?.user.id]);

  return (
    <MeContext.Provider value={{ me, loading, error, refresh: load }}>{children}</MeContext.Provider>
  );
}

export function useMe() {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error('useMe must be used inside MeProvider');
  return ctx;
}
